import DataTable from "organisms/data-tables/data-table";
import GroupMemberRecord from "models/view-models/group-member-record";
import React, { useState } from "react";
import { t } from "utilities/localization-utils";
import Loader from "atoms/loaders/loader";
import ManageTeamMemberListTableRow from "./manage-team-member-list-table-row";
import uuid from "uuid";
import useTeamOnboardingAlerts from "utilities/hooks/domain/team-onboarding/use-team-onboarding-notifications";
import { useGlobalState } from "utilities/contexts/use-global-state-context";

// -----------------------------------------------------------------------------------------
// #region Interfaces
// -----------------------------------------------------------------------------------------

interface ManageTeamMembersListProps {
    teamOwner?: GroupMemberRecord;
    teamMembersToMigrate: GroupMemberRecord[];
    emailsToInvite: string[];
    onUpdateTeamMembersToMigrate: (
        updatedTeamMembersList: GroupMemberRecord[]
    ) => void;
    onUpdateEmailsToInvite: (updatedEmailsToInviteList: string[]) => void;
    areGroupMembersLoading: boolean;
    totalSeatsCurrentlyTaken: number;
    totalSeatsRemaining: number;
}

// #endregion Interfaces

// -----------------------------------------------------------------------------------------
// #region Constants
// -----------------------------------------------------------------------------------------

const CLASS_NAME = "c-manage-team-settings";

// #endregion Constants

// -----------------------------------------------------------------------------------------
// #region Component
// -----------------------------------------------------------------------------------------

const ManageTeamMembersList: React.FC<ManageTeamMembersListProps> = (props) => {
    const {
        teamOwner,
        teamMembersToMigrate,
        emailsToInvite,
        onUpdateTeamMembersToMigrate,
        onUpdateEmailsToInvite,
        totalSeatsCurrentlyTaken,
        totalSeatsRemaining,
        areGroupMembersLoading = false,
    } = props;

    // Used for UI only; not involved in the migration process
    const [teamMembersToRemove, setTeamMembersToRemove] = useState<
        GroupMemberRecord[]
    >([]);
    const [emailsToNotInvite, setEmailsToNotInvite] = useState<string[]>([]);
    const { showNotEnoughSeatsAlert } = useTeamOnboardingAlerts();

    const { globalState } = useGlobalState();
    const isCurrentUserOwner =
        globalState?.currentIdentity?.user?.email === teamOwner?.email;

    const handleRemoveEmail = (emailToRemove: string) => {
        setEmailsToNotInvite([...emailsToNotInvite, emailToRemove]);
        onUpdateEmailsToInvite(
            emailsToInvite.filter((email) => email !== emailToRemove)
        );
    };
    const handleRestoreEmail = (emailToRestore: string) => {
        if (totalSeatsRemaining <= 0) {
            showNotEnoughSeatsAlert();
        } else {
            setEmailsToNotInvite(
                emailsToNotInvite.filter((email) => email !== emailToRestore)
            );
            onUpdateEmailsToInvite([...emailsToInvite, emailToRestore]);
        }
    };

    const handleRemoveTeamMember = (memberToRemove: GroupMemberRecord) => {
        setTeamMembersToRemove([...teamMembersToRemove, memberToRemove]);
        onUpdateTeamMembersToMigrate(
            teamMembersToMigrate.filter(
                (member) => member.email !== memberToRemove.email
            )
        );
    };
    const handleRestoreTeamMember = (memberToRestore: GroupMemberRecord) => {
        if (totalSeatsRemaining <= 0) {
            showNotEnoughSeatsAlert();
        } else {
            setTeamMembersToRemove(
                teamMembersToRemove.filter(
                    (member) => member.email !== memberToRestore.email
                )
            );
            onUpdateTeamMembersToMigrate([
                ...teamMembersToMigrate,
                memberToRestore,
            ]);
        }
    };

    return (
        <div>
            {areGroupMembersLoading ? (
                <Loader />
            ) : (
                <DataTable cssClassName={`${CLASS_NAME}__table`}>
                    <thead>
                        <tr>
                            <th>
                                {totalSeatsCurrentlyTaken}{" "}
                                {totalSeatsCurrentlyTaken === 1
                                    ? t("team-member")
                                    : t("team-member_plural")}
                            </th>
                            <th>{t("email")}</th>
                            <th>{t("lastActive")}</th>
                            <th>
                                <span className="sr-only">{t("delete")}</span>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {/* Team owner. They are not included in the team members to migrate, so manually added here for UI only */}
                        <ManageTeamMemberListTableRow
                            isOwner
                            key={uuid()}
                            id={teamOwner?.legacyId}
                            fullName={`${teamOwner?.getFirstAndLastName()} ${isCurrentUserOwner ? t("(you)") : `(${t("owner")})`}`}
                            lastActiveDate={teamOwner?.formatLastActive()}
                            emailAddress={teamOwner?.email ?? ""}
                        />

                        {emailsToInvite?.map((email, index) => (
                            <ManageTeamMemberListTableRow
                                key={uuid()}
                                emailAddress={email}
                                onRemove={handleRemoveEmail}
                                onRestore={handleRestoreEmail}
                            />
                        ))}

                        {teamMembersToMigrate?.map(
                            (member: GroupMemberRecord, index) => (
                                <ManageTeamMemberListTableRow
                                    key={uuid()}
                                    fullName={member.getFirstAndLastName()}
                                    lastActiveDate={member.formatLastActive()}
                                    id={member.legacyId}
                                    emailAddress={member.email}
                                    onRemove={() =>
                                        handleRemoveTeamMember(member)
                                    }
                                    onRestore={() =>
                                        handleRestoreTeamMember(member)
                                    }
                                />
                            )
                        )}

                        {teamMembersToRemove?.map(
                            (member: GroupMemberRecord) => (
                                <ManageTeamMemberListTableRow
                                    key={uuid()}
                                    isRemoved={true}
                                    fullName={member.getFirstAndLastName()}
                                    lastActiveDate={member.formatLastActive()}
                                    id={member.legacyId}
                                    emailAddress={member.email}
                                    onRemove={() =>
                                        handleRemoveTeamMember(member)
                                    }
                                    onRestore={() =>
                                        handleRestoreTeamMember(member)
                                    }
                                />
                            )
                        )}

                        {emailsToNotInvite?.map((email) => (
                            <ManageTeamMemberListTableRow
                                key={uuid()}
                                emailAddress={email}
                                onRemove={handleRemoveEmail}
                                onRestore={handleRestoreEmail}
                                isRemoved={true}
                            />
                        ))}
                    </tbody>
                </DataTable>
            )}
        </div>
    );
};

// #endregion Component

export default ManageTeamMembersList;
